/**
 * Layout.tsx — app shell. Fixed-width sidebar (nav + brand + account
 * menu), a slim topbar (breadcrumb-ish page context + ⌘K search trigger),
 * and the routed page content. Manager-only links are hidden from editors
 * (UX convenience only — see ProtectedRoute.tsx for the real enforcement
 * note).
 */
import { useReviewInbox } from "../hooks/useReviewInbox";
import { VoiceDictation } from "@/components/VoiceDictation";
import { Suspense, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, LogOut, Menu, Search, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { cn } from "@/lib/cn";
import { ALL_NAV_ITEMS, NAV_GROUPS, currentItem, type NavItem } from "../lib/navigation";
import { isScopedPath, scopeSearch } from "../lib/scope";
import { ScopeBar } from "./ScopeBar";
import { Avatar } from "./ui/avatar";
import { CommandPalette } from "./CommandPalette";
import { SiteSwitcher } from "./SiteSwitcher";
import { Logo } from "./brand/Logo";
import { Skeleton } from "./ui/skeleton";
import { prefetchAllWhenIdle, prefetchPath } from "../routeLoaders";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

function NavRow({ item, active, badge, search }: { item: NavItem; active: boolean; badge?: number; search: string }) {
  return (
    <Link
      to={`${item.to}${search}`}
      aria-current={active ? "page" : undefined}
      onMouseEnter={() => prefetchPath(item.to)}
      onFocus={() => prefetchPath(item.to)}
      className={cn(
        "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-md font-medium transition-colors",
        active ? "text-neutral-900" : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
      )}
    >
      {active && <motion.span layoutId="nav-active" className="absolute inset-0 rounded-lg bg-accent-50 ring-1 ring-inset ring-accent-100" transition={{ type: "spring", bounce: 0.2, duration: 0.4 }} />}
      <item.icon className="relative z-10 size-5" strokeWidth={2} />
      <span className="relative z-10 flex-1">{item.label}</span>
      {badge ? <Badge variant="warning" className="relative z-10 px-1.5 tabular-nums" aria-label={`${badge} waiting`}>{badge > 99 ? "99+" : badge}</Badge> : null}
    </Link>
  );
}

function PageFallback() {
  return (
    <div aria-busy="true" aria-label="Loading page" className="space-y-4">
      <Skeleton className="h-7 w-56" />
      <Skeleton className="h-4 w-80" />
      <Skeleton className="mt-6 h-64 w-full" />
    </div>
  );
}

export function Layout() {
  const { user, logout, can } = useAuth();
  const isManager = user?.role === "manager";
  const location = useLocation();
  const allowed = (item: NavItem) => (item.managerOnly ? isManager : item.capability ? can(item.capability) : true);
  const inbox = useReviewInbox();
  const [searchParams] = useSearchParams();
  const visible = ALL_NAV_ITEMS.filter(allowed);
  const current = currentItem(visible, location.pathname);
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(allowed) })).filter((g) => g.items.length > 0);
  // Sidebar groups fold away. The choice is remembered on this device; the group holding the current page is always open.
  const [folded, setFolded] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem("nav-folded") ?? "{}") as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const currentGroup = groups.find((g) => g.items.some((i) => i === current))?.title ?? null;
  const isOpen = (title: string) => title === currentGroup || !folded[title];
  const toggle = (title: string) => {
    const next = { ...folded, [title]: isOpen(title) };
    setFolded(next);
    try {
      localStorage.setItem("nav-folded", JSON.stringify(next));
    } catch {
      /* private window: the choice just isn't remembered */
    }
  };
  const scoped = isScopedPath(location.pathname) || location.pathname === "/review";
  const reviewWaiting = inbox.counts.ad + inbox.counts.social;
  // Below the `lg` breakpoint the sidebar is a slide-in drawer instead of a
  // permanent column, so phones and small tablets get the full width.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setNavOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  useEffect(() => {
    prefetchAllWhenIdle();
  }, []);

  return (
    // The shell is exactly one screen tall; only <main> scrolls. That is what
    // keeps the sidebar fixed while long pages scroll.
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      <CommandPalette />

      {navOpen && <div className="fixed inset-0 z-30 bg-neutral-900/40 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex h-screen w-64 shrink-0 flex-col border-r border-neutral-200 bg-white transition-transform duration-200 ease-out motion-reduce:transition-none",
          "lg:static lg:z-auto lg:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 shrink-0 items-center px-5">
          <Link to="/" aria-label="DemandHub CMS home" className="flex items-center gap-2.5">
            <Logo variant="full" className="h-9 w-auto" title="DemandHub" />
            <Badge>CMS</Badge>
          </Link>
        </div>

        <SiteSwitcher />

        <div className="mx-3 my-2.5 shrink-0 border-t border-neutral-100" />

        <nav aria-label="Main" className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-3 pb-2">
          {groups.map((g, gi) => (
            <div key={g.title ?? `g${gi}`} className={cn("space-y-0.5", gi > 0 && "mt-3")}>
              {g.title && (
                <button
                  type="button"
                  aria-expanded={isOpen(g.title)}
                  aria-controls={`nav-${g.title}`}
                  onClick={() => toggle(g.title!)}
                  className="flex w-full items-center justify-between rounded-md px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 hover:text-neutral-800"
                >
                  {g.title}
                  <ChevronDown className={cn("size-4 transition-transform", !isOpen(g.title) && "-rotate-90")} aria-hidden />
                </button>
              )}
              <div id={g.title ? `nav-${g.title}` : undefined} hidden={!!g.title && !isOpen(g.title)} className="space-y-0.5">
              {g.items.map((item) => (
                <NavRow
                  key={item.to}
                  item={item}
                  active={item === current}
                  badge={item.badge === "review" ? reviewWaiting : undefined}
                  search={item.to.startsWith("/ads/") || item.to.startsWith("/social/") ? scopeSearch(item.to, searchParams) : ""}
                />
              ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-neutral-100 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-accent-500/50">
              <Avatar name={user?.name ?? "?"} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium text-neutral-900">{user?.name}</span>
                <span className="block truncate text-xs capitalize text-neutral-400">{user?.role}</span>
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/account/security">
                  <ShieldCheck className="size-4" />
                  My security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => logout()} className="text-danger-600 focus:bg-danger-50 focus:text-danger-700">
                <LogOut className="size-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex h-screen min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-neutral-200 bg-white/80 px-4 backdrop-blur sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Open menu"
              className="-ml-1.5 rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 lg:hidden"
            >
              <Menu className="size-5" />
            </button>
            <p className="truncate text-lg font-semibold text-neutral-800">{current?.label ?? ""}</p>
          </div>
          <div className="flex items-center gap-2">
          <VoiceDictation />
          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
            className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm text-neutral-500 ring-1 ring-inset ring-neutral-200 transition-colors hover:bg-neutral-100"
          >
            <Search className="size-3.5" />
            <span className="hidden sm:inline">Search…</span>
            <kbd className="hidden rounded sm:inline border border-neutral-200 bg-white px-1 font-sans text-xs">⌘K</kbd>
          </button>
          </div>
        </header>

        {scoped && <ScopeBar />}

        <main className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-6 sm:px-8 sm:pb-12 sm:pt-8">
          {/* The page fades in from 0.85, not 0, so a route change never passes through an empty screen. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.12 }}
            className="mx-auto max-w-6xl"
          >
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
